import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingBantryBayPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Bantry Bay Luxury Painters - High-End Residential Painting | Apartment Specialists',
    description: 'Luxury painting services in Bantry Bay, Fresnaye, and Sea Point. Specialized in high-end apartments and residential properties. Over 16 years serving Atlantic Seaboard with premium finishes.',
    keywords: 'painters Bantry Bay, luxury painters Bantry Bay, apartment painting, high-end painting, residential painting, Atlantic Seaboard painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Bantry Bay',
          description: 'Luxury painting services for high-end properties',
          address: 'Bantry Bay, Cape Town',
          priceRange: '$$$',
          serviceArea: ['Bantry Bay', 'Fresnaye', 'Sea Point', 'Clifton', 'Camps Bay']
        }),
        generateServiceSchema({
          name: 'Luxury Painting Services',
          description: 'High-end apartment and residential painting',
          provider: 'Painters Bantry Bay',
          areaServed: ['Bantry Bay', 'Fresnaye', 'Sea Point'],
          serviceType: ['Luxury Apartment Painting', 'Residential Painting', 'High-End Finishes']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Bantry Bay Painters",
    location: "Bantry Bay, Cape Town",
    website: "https://bantrybaypainters.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/d7bd5e2dd9927efdd35848cd79380c0d.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=luxury%20penthouse%20interior%20with%20floor%20to%20ceiling%20windows%20ocean%20views%20freshly%20painted%20white%20walls%20modern%20furniture%20elegant%20design%20professional%20finish&width=800&height=600&seq=bantry-gallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=apartment%20building%20balcony%20waterproofing%20work%20professional%20painters%20applying%20protective%20coating%20ocean%20view%20modern%20architecture%20coastal%20property&width=800&height=600&seq=bantry-gallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=luxury%20apartment%20complex%20exterior%20painting%20scaffolding%20professional%20team%20modern%20high%20rise%20building%20pristine%20white%20finish%20ocean%20backdrop&width=800&height=600&seq=bantry-gallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20apartment%20interior%20living%20room%20freshly%20painted%20feature%20wall%20designer%20furniture%20ocean%20views%20modern%20luxury%20finish%20professional%20quality&width=800&height=600&seq=bantry-gallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Penthouse Painting",
        description: "Specialized painting services for luxury penthouses and high-end apartments. We understand the unique requirements of premium properties and deliver exceptional results that match the sophistication of your space.",
        features: ["Floor-to-ceiling painting", "Feature wall designs", "Premium paint finishes", "Minimal disruption service"]
      },
      {
        title: "Luxury Apartments",
        description: "Professional painting solutions for upscale apartment complexes and individual units. Our team works efficiently while maintaining the highest standards of quality and cleanliness.",
        features: ["Interior apartment painting", "Common area painting", "Balcony treatments", "Designer color consultation"]
      },
      {
        title: "Balcony Waterproofing",
        description: "Expert waterproofing and protective coating services for balconies and outdoor areas. Essential for coastal properties to prevent water damage and maintain structural integrity.",
        features: ["Waterproof membrane application", "Crack repair and sealing", "Anti-slip coatings", "Salt damage prevention"]
      }
    ],
    serviceAreas: ["Bantry Bay", "Fresnaye", "Sea Point", "Clifton", "Camps Bay", "Green Point"],
    about: "Bantry Bay Painters is the premier painting contractor specializing in luxury apartments and penthouses in Cape Town's most exclusive coastal suburb. With over 14 years of experience in high-end residential painting, we have established ourselves as the trusted choice for discerning property owners and body corporates in Bantry Bay and surrounding Atlantic Seaboard areas.",
    aboutExtended: [
      "Our expertise extends across all aspects of luxury apartment painting, from individual penthouse units to large-scale apartment complex projects. We understand the unique challenges of working in high-rise buildings and coastal environments, where salt air and ocean winds require specialized painting techniques and premium materials.",
      "What distinguishes us in the luxury property market is our commitment to using only the finest quality paints and materials specifically formulated for coastal conditions. Our painters are highly trained professionals who understand the importance of precision, cleanliness, and minimal disruption in occupied buildings. We work closely with body corporates, property managers, and individual owners to ensure seamless project execution.",
      "We specialize in waterproofing solutions for balconies and outdoor areas, a critical service for coastal properties. Our comprehensive approach includes thorough surface preparation, crack repair, waterproof membrane application, and protective coatings that withstand the harsh Atlantic coastal environment while maintaining aesthetic appeal."
    ],
    ourApproach: [
      "Detailed property assessment and color consultation",
      "Comprehensive surface preparation and repairs",
      "Premium coastal-grade paints and materials",
      "Professional application with minimal disruption",
      "Thorough waterproofing for balconies and outdoor areas",
      "Complete cleanup and final quality inspection"
    ],
    whyChoose: [
      "14+ years luxury property experience",
      "Coastal painting specialists",
      "Premium quality materials",
      "Body corporate approved",
      "Waterproofing experts",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "David Goldstein",
        rating: 5,
        text: "Exceptional service for our penthouse renovation. The team was professional, punctual, and the quality of work is outstanding. They understood exactly what we needed for our luxury property."
      },
      {
        name: "Catherine van Rensburg",
        rating: 5,
        text: "As a body corporate trustee, I highly recommend Bantry Bay Painters. They completed our apartment complex exterior painting on time and within budget. Excellent communication throughout."
      },
      {
        name: "Michael Stein",
        rating: 5,
        text: "The balcony waterproofing work was superb. No more leaks and the finish looks fantastic. They really know how to handle coastal property challenges."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Bantry Bay Painters</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Bantry Bay Painters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in Cape Town's Atlantic Seaboard:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized painting services tailored for luxury apartments and penthouses in Bantry Bay.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Bantry Bay, Fresnaye, Sea Point</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Bantry Bay</li>
                <li>Sea Point</li>
                <li>Clifton</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
